import { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { PAYMENT_QR_LABELS } from '../constants/bookingConst';
import '../css/PaymentQrDisplay.css';

/**
 * A payment QR shown full screen — big enough to scan from another phone, or
 * to screenshot — with its own Download button.
 *
 * Portalled to <body> so the fixed overlay covers the whole screen even when
 * the checkout card it opens from sits inside a transformed/animated parent.
 * Per CLAUDE.md > Behavior, a tap on the backdrop does NOT close it: the ×
 * button (or Escape on a keyboard) is the way out.
 *
 * @param {object} props
 * @param {boolean} props.isOpen
 * @param {{name: string, url: string}|null} props.method - the QR on show
 * @param {() => void} props.onClose
 * @param {() => void} props.onDownload - saves the QR (PaymentQrDisplay owns the download)
 * @param {boolean} [props.downloading] - a download is in progress
 * @param {string} [props.error] - why the last download failed, if it did
 * @returns {JSX.Element|null}
 */
function QrFullscreenViewer({ isOpen, method, onClose, onDownload, downloading = false, error = '' }) {
  // Escape closes, and the page behind stops scrolling while the viewer is
  // up — on a phone a scroll gesture over the QR would otherwise move the
  // checkout card underneath it.
  useEffect(() => {
    if (!isOpen) return undefined;
    const handleKey = (event) => {
      if (event.key === 'Escape') onClose();
    };
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', handleKey);
    return () => {
      window.removeEventListener('keydown', handleKey);
      document.body.style.overflow = previousOverflow;
    };
  }, [isOpen, onClose]);

  if (!isOpen || !method) return null;

  return createPortal(
    <div className="pqr-viewer" role="dialog" aria-modal="true" aria-label={`${method.name} payment QR`}>
      <div className="pqr-viewer-head">
        <span className="pqr-viewer-title">{method.name}</span>
        <button type="button" className="pqr-viewer-close" onClick={onClose} aria-label="Close">&times;</button>
      </div>
      <div className="pqr-viewer-body">
        <img className="pqr-viewer-img" src={method.url} alt={`${method.name} payment QR`} />
      </div>
      <div className="pqr-viewer-foot">
        <button
          type="button"
          className="pqr-download-btn pqr-download-btn-lg"
          onClick={onDownload}
          disabled={downloading}
        >
          {downloading ? PAYMENT_QR_LABELS.DOWNLOADING : PAYMENT_QR_LABELS.DOWNLOAD}
        </button>
        {error && <p className="pqr-error pqr-error-on-dark">{error}</p>}
      </div>
    </div>,
    document.body,
  );
}

export default QrFullscreenViewer;
